import React, { useEffect, useState } from 'react';
import { confirmAction } from '../../utils/confirm';
import { GatewayRecord, fetchApiKeys, createApiKey, deleteApiKey, ApiKeyRecord } from '../../services/api';

interface Props {
    gateways: GatewayRecord[];
    onError: (message: string) => void;
}

const inputStyle: React.CSSProperties = {
    padding: '7px 10px', fontSize: '13px', border: '1px solid #ccc',
    borderRadius: '4px', width: '100%', boxSizing: 'border-box',
};

const btnStyle = (color: string, bg: string, border: string): React.CSSProperties => ({
    padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
    backgroundColor: bg, color, border: `1px solid ${border}`, borderRadius: '4px', fontWeight: 'bold',
});

const fieldLabel: React.CSSProperties = { fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' };

// API-nycklar för maskin-till-maskin: gateway-styrning eller heartbeat-sensor.
const ApiKeysSection: React.FC<Props> = ({ gateways, onError }) => {
    const [apiKeys, setApiKeys]         = useState<ApiKeyRecord[]>([]);
    const [formVisible, setFormVisible] = useState(false);
    const [label, setLabel]             = useState('');
    const [keyGateways, setKeyGateways] = useState<string[]>([]);
    const [mode, setMode]               = useState<'gateway' | 'heartbeat'>('gateway');
    const [deviceName, setDeviceName]   = useState('');
    const [heartbeatInterval, setHeartbeatInterval]       = useState('300');
    const [generatedKey, setGeneratedKey] = useState<string | null>(null);
    const [saving, setSaving]           = useState(false);

    const load = async () => {
        try { setApiKeys(await fetchApiKeys()); } catch { /* inte kritiskt */ }
    };

    useEffect(() => { load(); }, []);

    const openForm = () => {
        setFormVisible(true); setGeneratedKey(null); setLabel(''); setKeyGateways([]);
        setMode('gateway'); setDeviceName(''); setHeartbeatInterval('300');
    };

    const canSubmit = !!label.trim() && (mode === 'gateway' ? keyGateways.length > 0 : !!deviceName.trim());

    const create = async () => {
        setSaving(true);
        try {
            const result = mode === 'gateway'
                ? await createApiKey(label.trim(), keyGateways)
                : await createApiKey(label.trim(), [], {
                    deviceName: deviceName.trim(),
                    heartbeatIntervalSeconds: Math.max(0, parseInt(heartbeatInterval, 10) || 0),
                });
            setGeneratedKey(result.key);
            await load();
        } catch (e: any) {
            onError(e.response?.data?.error || e.message || 'Misslyckades att skapa nyckel');
            setFormVisible(false);
        } finally { setSaving(false); }
    };

    const remove = async (k: ApiKeyRecord) => {
        if (!confirmAction(`Ta bort nyckeln "${k.label}"?`)) return;
        try { await deleteApiKey(k.id); await load(); }
        catch (e: any) { onError(e.message || 'Misslyckades att ta bort nyckel'); }
    };

    return (
        <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', maxWidth: '680px' }}>
                <h2 style={{ margin: 0, fontSize: '18px' }}>API-nycklar (M2M)</h2>
                <button onClick={openForm} style={btnStyle('white', '#28a745', '#28a745')}>+ Ny nyckel</button>
            </div>

            {formVisible && !generatedKey && (
                <div style={{ background: '#f0f4ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '16px', marginBottom: '16px', maxWidth: '680px' }}>
                    <div style={{ marginBottom: '10px' }}>
                        <label style={fieldLabel}>Etikett</label>
                        <input style={inputStyle} placeholder="t.ex. Tvättstugaapp" value={label}
                            onChange={e => setLabel(e.target.value)} />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ ...fieldLabel, marginBottom: '6px' }}>Typ</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {([
                                { mode: 'gateway',   label: 'Gateway-styrning' },
                                { mode: 'heartbeat', label: 'Heartbeat (M2M-sensor)' },
                            ] as const).map(opt => (
                                <button key={opt.mode} type="button" onClick={() => setMode(opt.mode)}
                                    style={btnStyle(
                                        mode === opt.mode ? 'white' : '#555',
                                        mode === opt.mode ? '#004085' : '#e9ecef',
                                        mode === opt.mode ? '#004085' : '#ccc',
                                    )}>
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {mode === 'gateway' ? (
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ ...fieldLabel, marginBottom: '6px' }}>Gateway-åtkomst</label>
                            {gateways.length === 0 && <span style={{ fontSize: '12px', color: '#888' }}>Inga gateways registrerade.</span>}
                            {gateways.map(gw => (
                                <label key={gw.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', fontSize: '13px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={keyGateways.includes(gw.id)}
                                        onChange={e => setKeyGateways(prev =>
                                            e.target.checked ? [...prev, gw.id] : prev.filter(g => g !== gw.id)
                                        )} />
                                    {gw.name} <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#666' }}>({gw.id})</span>
                                </label>
                            ))}
                        </div>
                    ) : (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={fieldLabel}>Enhetsnamn</label>
                                <input style={inputStyle} placeholder="t.ex. Site-agent Tvättstuga" value={deviceName}
                                    onChange={e => setDeviceName(e.target.value)} />
                            </div>
                            <div>
                                <label style={fieldLabel}>Förväntat heartbeat-intervall (sekunder)</label>
                                <input style={inputStyle} type="number" min={0} placeholder="300" value={heartbeatInterval}
                                    onChange={e => setHeartbeatInterval(e.target.value)} />
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    Enheten skapas som M2M-sensor. Larm utlöses om ingen heartbeat tas emot inom detta intervall (0 = ingen offline-bevakning).
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button disabled={saving || !canSubmit} onClick={create}
                            style={{ ...btnStyle('white', '#004085', '#004085'), opacity: canSubmit ? 1 : 0.5 }}>
                            {saving ? 'Skapar…' : 'Skapa'}
                        </button>
                        <button onClick={() => setFormVisible(false)} style={btnStyle('#555', '#e9ecef', '#ccc')}>Avbryt</button>
                    </div>
                </div>
            )}

            {generatedKey && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '16px', marginBottom: '16px', maxWidth: '680px' }}>
                    <strong style={{ fontSize: '13px' }}>⚠ Spara nyckeln nu — den visas aldrig igen!</strong>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all', margin: '10px 0', padding: '10px', background: '#fffde7', border: '1px solid #ffe082', borderRadius: '4px' }}>
                        {generatedKey}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => { navigator.clipboard.writeText(generatedKey); }}
                            style={btnStyle('#004085', '#e8f0fe', '#b8daff')}>Kopiera</button>
                        <button onClick={() => { setGeneratedKey(null); setFormVisible(false); }}
                            style={btnStyle('#555', '#e9ecef', '#ccc')}>Klar</button>
                    </div>
                </div>
            )}

            {apiKeys.length === 0 && !formVisible && (
                <p style={{ fontSize: '13px', color: '#888', maxWidth: '680px' }}>Inga API-nycklar ännu.</p>
            )}
            {apiKeys.map(k => (
                <div key={k.id} style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '12px 16px', marginBottom: '8px', maxWidth: '680px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{k.label}</span>
                        <div style={{ fontSize: '12px', color: '#555', marginTop: '3px' }}>
                            {k.device_id
                                ? <>Heartbeat-enhet: <span style={{ fontWeight: 'bold' }}>{k.device_name || `#${k.device_id}`}</span></>
                                : <>Gateways: <span style={{ fontFamily: 'monospace' }}>{Array.isArray(k.gateway_ids) ? k.gateway_ids.join(', ') : k.gateway_ids}</span></>
                            }
                        </div>
                        <div style={{ fontSize: '11px', color: '#888' }}>
                            Skapad: {new Date(k.created_at).toLocaleString('sv-SE')}
                            {k.last_used_at && <> · Senast använd: {new Date(k.last_used_at).toLocaleString('sv-SE')}</>}
                        </div>
                    </div>
                    <button onClick={() => remove(k)} style={btnStyle('#721c24', '#f8d7da', '#f5c6cb')}>Ta bort</button>
                </div>
            ))}
        </div>
    );
};

export default ApiKeysSection;
